import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  ProjectTeamMemberResponse,
  deleteProjectTeamMember,
  getProjectTeamMembersByProjectId,
} from '@/services/ProjectTeamMemberService';
import { useTranslation } from 'react-i18next';
import { AssignedTask } from '@/enums/AssignedTask';

function ProjectTeamMembers() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('ProjectTeamMembers.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('ProjectTeamMembers.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteProjectTeamMember.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('ProjectTeamMembers.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteProjectTeamMember}
          />
        ),
      },
      {
        Header: t('ProjectTeamMembers.assignedTaskColumn'),
        accessor: 'assignedTask',
        //Filter: SelectColumnFilter,
        Cell: ({ cell: { value } }) => {
          const frontendState = AssignedTask[value];

          return (
            <div
              className={`min-w-50 text-md w-40 rounded-md  px-2.5  py-2.5 text-center `}
            >
              {t(`AssignedTask.${frontendState}`)}
            </div>
          );
        },
      },
      {
        Header: t('ProjectTeamMembers.employeeNameColumn'),
        accessor: 'employeeName',
        // Filter: SelectColumnFilter,
      },

      {
        Header: t('Appointments.department'),
        accessor: 'departmentName',
      },
      {
        Header: t('Appointments.branch'),
        accessor: 'branchName',
      },
      {
        Header: t('Employee.email'),
        accessor: 'email',
        //Filter: SelectColumnFilter,
      },
      {
        Header: t('SupplierOrderForm.phoneLabel'),
        accessor: 'phoneNumber',
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: projectTeamMembers,
  } = useQuery<ProjectTeamMemberResponse | undefined>(
    ['projectTeamMembers', pageIndex, pageSize, search],
    () =>
      getProjectTeamMembersByProjectId({
        projectId: id!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      enabled: id !== null,
    }
  );

  const handleDeleteProjectTeamMember = useMutation(
    async (id: string) => {
      await deleteProjectTeamMember(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'projectTeamMembers',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((projectTeamMembers?.count ?? 0) / pageSize);
  }, [pageSize, projectTeamMembers]);
  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('ProjectTeamMembers.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={projectTeamMembers?.count}
            data={projectTeamMembers?.projectTeamMembers ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default ProjectTeamMembers;
